"use client";

import { cn } from "@/lib/utils";
import { movementLabel, type MovementState } from "@/lib/keyword-explorer";

/**
 * How a position moved since the step before it, in the one style both keyword
 * surfaces use.
 *
 * The word carries the news and the sign carries the direction: a delta is
 * `before - after`, so 14 -> 3 is +11 and reads "up 11". A smaller rank is a
 * better result, which is why the arrow and the colour follow the sign rather
 * than the raw position.
 *
 * "new" and "gone" are the split of the dash that matters. An app absent from
 * one end of the comparison has no delta, and at the depth a SERP is read that
 * is the common case, so "—" for an arrival and "—" for a disappearance would
 * be one mark for opposite news.
 *
 * It lives here rather than in the app detail panel because the owner asked for
 * the Keywords page to render movement the same way that panel already does.
 * Two copies of one renderer is the defect that request is about, so the panel
 * imports this and keeps no copy of its own. Its rendered output is unchanged:
 * the panel passes no `firstDay`, which is the only arm this file adds.
 */
export function KeywordMovement({
  state,
  delta,
  previousRank,
  previousOn,
  terminal,
  firstDay = false,
}: {
  state: MovementState;
  delta: number | null;
  previousRank: number | null;
  previousOn: string | null;
  terminal: boolean;
  /**
   * True when the step being described is the oldest day on the axis. The
   * keywords page scrubs to a day and day 0 has nothing before it, which is a
   * different silence from "swept twice and absent from both".
   */
  firstDay?: boolean;
}) {
  const was = previousRank !== null && previousOn ? ` Was #${previousRank} on ${previousOn}.` : "";

  // Everything except a real delta is a word, and each word gets the sentence
  // that says which silence it is. "—" has more than one meaning and the reader
  // deserves to know which: nothing before this day at all, the keyword has been
  // swept once, or it has been swept twice and this app was in neither result.
  const words: Partial<Record<MovementState, [string, string]>> = {
    new: ["new", `Not in the results on the previous sweep${previousOn ? ` (${previousOn})` : ""}.`],
    gone: ["gone", `Not in the latest results for this keyword.${was}`],
    flat: ["no change", `Same position as the previous sweep.${was}`],
    none: [
      "—",
      firstDay
        ? "The first day on record for this keyword, so there is nothing before it to compare."
        : previousOn
          ? `No position on either of the last two sweeps of this keyword (${previousOn} and later).`
          : "This keyword has only been swept once, so there is nothing to compare.",
    ],
  };
  // The `delta === null` arm is what narrows delta to a number below, not a
  // second reading of the state: an up or down state always carries one, and if
  // it ever did not, a dash beats NaN.
  const word = words[state] ?? (delta === null ? words.none : undefined);
  if (word) {
    return (
      <span className="text-[12px] text-muted-foreground" title={word[1]}>
        {word[0]}
      </span>
    );
  }

  const up = state === "up";
  return (
    <span
      className={cn(
        "text-[12px] font-semibold tabular-nums",
        terminal
          ? cn("font-mono", up ? "text-emerald-300" : "text-rose-300")
          : up
            ? "text-emerald-700"
            : "text-rose-700",
      )}
      title={`${movementLabel(delta)} since the previous sweep.${was}`}
    >
      {up ? "↑" : "↓"}
      {Math.abs(delta as number)}
    </span>
  );
}
